// src/components/theme/elements/ThemeNavMap.js

import React, { useState, useEffect, useRef } from 'react';
import { Group, Image as KonvaImage, Rect as KonvaRect } from 'react-konva';
import PropTypes from 'prop-types';
import Konva from 'konva';
import { toast } from 'react-toastify';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


/**
 * ThemeNavMapSettings Component
 * Provides UI controls to configure the ThemeNavMap element.
 * Includes additional color settings that do not affect the image.
 */
export const ThemeNavMapSettings = ({ element, updateElement }) => {

  /**
   * Handler for X position change.
   * Updates the X position of the element.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x) && x >= 0) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handler for Y position change.
   * Updates the Y position of the element.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y) && y >= 0) {
      updateElement(element.id, 'y', y);
    }
  };


  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* X Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px', width: '80px' }}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px', width: '80px' }}
          aria-label="Set Y Position"
        />
      </label>

      {/* Road Line Color */}
      <CustomColorPicker
        label="Road Line Color"
        color={element.roadLineColor}
        onChange={(value) => { updateElement(element.id, 'roadLineColor', value); }}
      />

      {/* Selected Road Color */}
      <CustomColorPicker
        label="Selected Road Color"
        color={element.selectedRoadColor}
        onChange={(value) => { updateElement(element.id, 'selectedRoadColor', value); }}
      />

      {/* Start Point Color */}
      <CustomColorPicker
        label="Start Point Colo"
        color={element.startPointColor}
        onChange={(value) => { updateElement(element.id, 'startPointColor', value); }}
      />

      {/* Destination Point Color */}
      <CustomColorPicker
        label="Destination Point Color"
        color={element.destinationPointColor}
        onChange={(value) => { updateElement(element.id, 'destinationPointColor', value); }}
      />

      {/* Reserved 1 Color */}
      <CustomColorPicker
        label="Reserved 1 Color"
        color={element.reserved1Color}
        onChange={(value) => { updateElement(element.id, 'reserved1Color', value); }}
      />

      {/* Reserved 2 Color */}
      <CustomColorPicker
        label="Reserved 2 Color"
        color={element.reserved2Color}
        onChange={(value) => { updateElement(element.id, 'reserved2Color', value); }}
      />

      {/* Reserved 3 Color */}
      <CustomColorPicker
        label="Reserved 3 Color"
        color={element.reserved3Color}
        onChange={(value) => { updateElement(element.id, 'reserved3Color', value); }}
      />

    </div>
  );
};

ThemeNavMapSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    roadLineColor: PropTypes.string,         // Road Line Color (optional)
    selectedRoadColor: PropTypes.string,     // Selected Road Color (optional)
    startPointColor: PropTypes.string,       // Start Point Color (optional)
    destinationPointColor: PropTypes.string, // Destination Point Color (optional)
    reserved1Color: PropTypes.string,        // Reserved 1 Color (optional) - Default to white
    reserved2Color: PropTypes.string,        // Reserved 2 Color (optional) - Default to white
    reserved3Color: PropTypes.string,        // Reserved 3 Color (optional) - Default to white
    isEnabled: PropTypes.bool,               // Enabled state (optional)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

/**
 * ThemeNavMap Component
 * Renders the GPS Map image on the canvas without any color modifications.
 */
const ThemeNavMap = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const [imageObj, setImageObj] = useState(null);
  const imageRef = useRef();

  // Fixed dimensions for the image
  const fixedWidth = 256;
  const fixedHeight = 147;

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  /**
   * Load the GPS Map image.
   * Sets the image object once loaded.
   */
  useEffect(() => {
    const img = new window.Image();
    img.src = './images_theme/nav-map.png'; // Path to your GPS map image
    img.crossOrigin = 'Anonymous'; // To handle cross-origin issues if any
    img.onload = () => {
      setImageObj(img);
      console.log('GPS Map image loaded successfully.', img);
    };
    img.onerror = () => {
      toast.error('Failed to load GPS Map image.');
    };
  }, []);

  /**
   * Cache the image and redraw the layer when imageObj changes.
   * Removed color dependencies to ensure colors don't affect the image.
   */
  useEffect(() => {
    if (imageObj && imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [imageObj]);

  /**
   * Handler for drag end event.
   * Updates the X and Y positions of the element.
   */
  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x));
    updateElement(element.id, 'y', Math.round(y));
  };

  /**
   * Re-apply cache and redraw when imageObj changes.
   */
  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer().batchDraw();
    }
  }, [imageObj]);

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed} // Make draggable only when render is false
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, fixedWidth, fixedHeight) }}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
      aria-label="GPS Map Indicator"
    >
      {imageObj && (
        <KonvaImage
          image={imageObj}
          width={fixedWidth} // Set fixed width
          height={fixedHeight} // Set fixed height
          // Removed filters and tintColor to ensure colors do not affect the image
          ref={imageRef}
          onLoad={() => {
            if (imageRef.current) {
              imageRef.current.cache();
              imageRef.current.getLayer().batchDraw();
            }
          }}
        />
      )}
      {/* Add the debug blue rectangle */}
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={fixedWidth}
          height={fixedHeight}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
          listening={false} // Ensure it doesn't capture pointer events
        />
      )}
    </Group>
  );
};

ThemeNavMap.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    usage: PropTypes.number.isRequired,
    roadLineColor: PropTypes.string,         // Road Line Color (optional)
    selectedRoadColor: PropTypes.string,     // Selected Road Color (optional)
    startPointColor: PropTypes.string,       // Start Point Color (optional)
    destinationPointColor: PropTypes.string, // Destination Point Color (optional)
    reserved1Color: PropTypes.string,        // Reserved 1 Color (optional)
    reserved2Color: PropTypes.string,        // Reserved 2 Color (optional)
    reserved3Color: PropTypes.string,        // Reserved 3 Color (optional)
    isEnabled: PropTypes.bool,               // Enabled state (optional)
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other properties as needed
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeNavMap;
